import axios from "axios";

export interface PastEvent {
  id: string;
  title: string;
  date: string;
  startTime?: string;
  endTime?: string;
  description: string;
  location: string;
  image: string;
  eventLink?: string;
  teacher?: string;
  category?: string;
  registrationLink?: string;
}

// Sheet IDs voor verschillende omgevingen
const SHEET_CONFIG = {
  development: {
    id: "1Ol8UqcOWUPetCpM5TKye0h8J0EPWvsDBimwZYBujvOA",
    name: "Form Responses 1",
  },
  production: {
    id: "1ecE0Al1xjSuPtNrqrX3N1xXlCJgGiYDKq455zwfMDFQ",
    name: "Form Responses 1",
  },
};

// Kolomindexen configuratie voor de spreadsheet
const COLUMN_INDEXES = {
  // Standaard kolomposities
  TIMESTAMP: 0,
  TITLE: 1,
  DATE: 2,
  DESCRIPTION: 3,
  LOCATION: 4,
  IMAGE: 5,
  EVENT_LINK: 6,
  // Nieuwe kolomposities
  START_TIME: 7,
  END_TIME: 8,
  TEACHER: 9,
  CATEGORY: 10,
  REGISTRATION_LINK: 11
};

// Bepaal de huidige omgeving - use import.meta.env for Vite compatibility
const ENV = import.meta.env.MODE || "development";
console.log("Current environment for pastEventService:", ENV);

// Selecteer de juiste sheet configuratie
const SHEET_ID = SHEET_CONFIG[ENV as keyof typeof SHEET_CONFIG].id;
const SHEET_NAME = SHEET_CONFIG[ENV as keyof typeof SHEET_CONFIG].name;

function formatDate(dateString: string): string {
  try {
    if (!dateString) return "";
    
    if (dateString.startsWith("Date(")) {
      const parts = dateString
        .replace("Date(", "")
        .replace(")", "")
        .split(",")
        .map(Number);

      // Correctie voor JavaScript maanden (0-11)
      const date = new Date(parts[0], parts[1] - 1, parts[2]);

      return date.toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }

    // Als het geen Date() string is, probeer het als normale datum te parsen
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }

    return dateString;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
}

function formatTime(timeString: string): string | undefined {
  try {
    if (!timeString) return undefined;
    
    if (timeString.startsWith("Date(")) {
      const parts = timeString
        .replace("Date(", "")
        .replace(")", "")
        .split(",")
        .map(Number);

      // Create a date object with the time components
      const date = new Date(1970, 0, 1, parts[3], parts[4], parts[5]);

      return date.toLocaleTimeString("nl-NL", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    
    // If it's already in a time format like "14:30", just return it
    if (/^\d{1,2}:\d{2}$/.test(timeString)) {
      return timeString;
    }
    
    // Try to parse it as a full date and extract the time
    const date = new Date(timeString);
    if (!isNaN(date.getTime())) {
      return date.toLocaleTimeString("nl-NL", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    
    return timeString;
  } catch (error) {
    console.error("Error formatting time:", error);
    return undefined;
  }
}

function formatUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;

  try {
    url = url.trim();

    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    if (url.startsWith("www.")) {
      return `https://${url}`;
    }

    if (!url.includes(".")) {
      return undefined;
    }

    return `https://www.${url}`;
  } catch (error) {
    console.error("Error formatting URL:", error);
    return undefined;
  }
}

/**
 * Haalt de juiste afbeeldings-URL op voor een evenement
 * Bevat ook specifieke overrides voor bepaalde evenementen
 */
function getImageUrl(url: string, title?: string, date?: string): string {
  try {
    console.log("Selecting image for event:", { title, date });
    
    // Specifieke hard-coded override voor Ivan Marinovic seminar
    // Zeer ruime matching gebruiken voor maximale zekerheid
    if (title && date) {
      const lowerTitle = title.toLowerCase();
      const lowerDate = date.toLowerCase();
      
      if ((lowerTitle.includes('ivan') || lowerTitle.includes('marinovic')) &&
          (lowerDate.includes('16') || lowerDate.includes('februari') || lowerDate.includes('february'))) {
        console.log("MATCH: Using specific image for Ivan Marinovic seminar");
        // Forceer het gebruik van deze afbeelding ongeacht wat er in de spreadsheet staat
        return "/images/seminarIvan2.jpg";
      }
    }
    
    if (!url) {
      return "/images/aikido-social.jpg";
    }
    
    if (url.startsWith("https://images.unsplash.com/") || url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    
    if (url.includes("unsplash.com/photos/")) {
      return "/images/aikido-social.jpg";
    }
    
    // Als het pad nog niet begint met /images/, voeg dit toe
    if (!url.startsWith("/images/") && !url.startsWith("/")) {
      return `/images/${url}`;
    }
    
    return url;
  } catch (error) {
    console.error("Error processing image URL:", error);
    return "/images/aikido-social.jpg";
  }
}

export async function fetchPastEvents(): Promise<PastEvent[]> {
  try {
    console.log(`Using ${ENV} sheet configuration for past events:`, {
      SHEET_ID,
      SHEET_NAME,
    });
    console.log(
      "Full Google Sheets URL:",
      `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=${SHEET_NAME}`
    );

    const response = await axios.get(
      `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=${SHEET_NAME}`
    );

    console.log("Raw Google Sheets response data type:", typeof response.data);
    console.log(
      "Raw Google Sheets response data length:",
      response.data.length
    );
    console.log(
      "Raw Google Sheets response data substring:",
      response.data.substring(0, 100)
    );

    // Add special handling to safely parse the Google Sheets response
    let data;
    try {
      data = JSON.parse(response.data.substring(47).slice(0, -2));
      console.log("Parsed Google Sheets data:", data);
    } catch (parseError) {
      console.error("Error parsing Google Sheets data:", parseError);
      console.warn(
        "Raw response substring for debugging:",
        response.data.substring(0, 200)
      );
      throw new Error(
        "Failed to parse Google Sheets data. The response format might have changed."
      );
    }

    console.log("Google Sheets data table structure:", data);

    if (!data.table || !data.table.rows) {
      console.warn("No rows found in Google Sheets response");
      return [];
    }

    console.log("Raw Google Sheets data for past events:", data.table.rows);

    // Define a type for Google Sheets row cell
    type GoogleSheetCell = { v: string | number | null | Date; f?: string };
    // Define a type for Google Sheets row
    type GoogleSheetRow = { c: GoogleSheetCell[] };

    return data.table.rows
      .filter((row: GoogleSheetRow) => row.c[COLUMN_INDEXES.TITLE]?.v) // Filter op titel
      .map((row: GoogleSheetRow, index: number) => {
        const dateStr = row.c[COLUMN_INDEXES.DATE]?.v || "";
        console.log("Processing past event row:", row);
        console.log("Date string:", dateStr);

        // Get all possible fields from the row
        const eventLinkStr = row.c[COLUMN_INDEXES.EVENT_LINK]?.v === null ? 
          undefined : String(row.c[COLUMN_INDEXES.EVENT_LINK]?.v || "");
        
        const startTimeStr = row.c[COLUMN_INDEXES.START_TIME]?.v === null ?
          undefined : String(row.c[COLUMN_INDEXES.START_TIME]?.v || "");
          
        const endTimeStr = row.c[COLUMN_INDEXES.END_TIME]?.v === null ?
          undefined : String(row.c[COLUMN_INDEXES.END_TIME]?.v || "");
        
        const teacherStr = row.c[COLUMN_INDEXES.TEACHER]?.v === null ?
          undefined : String(row.c[COLUMN_INDEXES.TEACHER]?.v || "");
          
        const categoryStr = row.c[COLUMN_INDEXES.CATEGORY]?.v === null ?
          undefined : String(row.c[COLUMN_INDEXES.CATEGORY]?.v || "");
          
        const registrationLinkStr = row.c[COLUMN_INDEXES.REGISTRATION_LINK]?.v === null ?
          undefined : String(row.c[COLUMN_INDEXES.REGISTRATION_LINK]?.v || "");
        
        return {
          id: index.toString(),
          title: String(row.c[COLUMN_INDEXES.TITLE]?.v || ""),
          date: formatDate(String(dateStr)),
          startTime: startTimeStr ? formatTime(startTimeStr) : undefined,
          endTime: endTimeStr ? formatTime(endTimeStr) : undefined,
          description: String(row.c[COLUMN_INDEXES.DESCRIPTION]?.v || ""),
          location: String(row.c[COLUMN_INDEXES.LOCATION]?.v || ""),
          image: getImageUrl(
            String(row.c[COLUMN_INDEXES.IMAGE]?.v || ""),
            String(row.c[COLUMN_INDEXES.TITLE]?.v || ""), 
            String(dateStr)
          ),
          eventLink: eventLinkStr ? formatUrl(eventLinkStr) : undefined,
          teacher: teacherStr || undefined,
          category: categoryStr || undefined,
          registrationLink: registrationLinkStr ? formatUrl(registrationLinkStr) : undefined,
        };
      });
  } catch (error) {
    console.error("Error fetching past events:", error);
    if (error instanceof Error) {
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
    }

    // Check if it's an Axios error
    if (axios.isAxiosError(error)) {
      console.error("Axios error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });

      // Network or CORS issue
      if (!error.response) {
        throw new Error(
          "Netwerkfout bij het verbinden met Google Sheets. Mogelijk een CORS probleem."
        );
      }

      // Authentication or permission issue
      if (error.response.status === 403) {
        throw new Error(
          "Geen toegang tot het Google Sheets document. Controleer de machtigingen."
        );
      }
    }

    throw new Error(
      "Er is een probleem opgetreden bij het ophalen van de eerdere evenementen. Probeer het later opnieuw."
    );
  }
}
